/**
 * Collision proxies (M10) — generic, no per-piece authoring.
 *
 * The channel's physical shape is the same law everywhere (floor at the
 * centerline, walls at ±0.35 track units rising 0.5 above the floor), so
 * proxies are SAMPLED from each segment's world-space centerline: short
 * overlapping slabs (floor + two walls) oriented by the local tangent.
 * Curved pieces just get more slabs. Near-vertical samples (drop-stack
 * shafts — the centerline dives through the hole) emit NOTHING: the marble
 * genuinely falls and the receiving channel below catches it. Island
 * masses are axis-aligned block boxes on the world grid.
 *
 * Shared by the client driver and the headless survivability tests —
 * one sampling law, one truth.
 */
import type { CurveSeg, LoopPath, Placement } from "../../shared/types.ts";
import { segLength, segPoint } from "../geom.ts";
import { TRACK_SCALE } from "./scale.ts";
import {
  affineApply,
  isIdentityAffine,
  type Quat,
  quatFromAxisAngle,
  quatMul,
  type SegmentFrame,
  segmentFrameAt,
  twistParam,
} from "../../shared/frame.ts";

/** Oriented slab: yaw about +y then pitch about local +x (shim contract).
 * `quat` (M16 reoriented chunks) supersedes yaw/pitch when present —
 * worldifyProxies folds them into one full orientation. */
export interface SlabProxy {
  kind: "slab";
  center: readonly [number, number, number];
  half: readonly [number, number, number];
  yaw: number;
  pitch: number;
  quat?: readonly [number, number, number, number];
}

/** Axis-aligned box (island blocks), yaw in quarter turns. */
export interface BoxProxy {
  kind: "box";
  center: readonly [number, number, number];
  half: readonly [number, number, number];
  quat?: readonly [number, number, number, number];
}

/** Smooth channel ribbon: one triangle mesh per contiguous strip (M11 —
 * slab chains made the marble contact awkwardly at every seam; a shared-
 * vertex extrusion is ONE continuous surface, and Box3D's identifyEdges
 * removes internal-edge collisions entirely). */
export interface MeshProxy {
  kind: "mesh";
  /** xyz triples. */
  vertices: number[];
  /** CCW triples into vertices. */
  indices: number[];
}

/** Bouncer pin (M13): a high-restitution cylinder — the mushroom bumper
 * on wide island decks. Bounce combines via max, so the pin repels the
 * marble regardless of shock zones. */
export interface BumperProxy {
  kind: "bumper";
  center: readonly [number, number, number];
  radius: number;
  height: number;
  restitution: number;
  quat?: readonly [number, number, number, number];
}

export type CollisionProxy = SlabProxy | BoxProxy | MeshProxy | BumperProxy;

/**
 * Present plan-space proxies in world space (M16 gravity reorientation):
 * mesh vertices transform per-vertex through the segment's frame law (a
 * ribbon crossing the twist run corkscrews smoothly between the two
 * frames); rigid shapes (slabs/boxes/bumpers) pick the frame at their
 * CENTER (set-pieces never straddle the run — its cells are reserved
 * bare straights) and carry the full orientation as a quaternion.
 * Identity frames return the input untouched.
 */
export function worldifyProxies(
  proxies: readonly CollisionProxy[],
  f: SegmentFrame,
): CollisionProxy[] {
  if (!f.twist && isIdentityAffine(f.entry)) return [...proxies];
  const frameAt = (p: readonly [number, number, number]) =>
    f.twist ? segmentFrameAt(f, twistParam(f, p)) : f.entry;
  return proxies.map((p): CollisionProxy => {
    if (p.kind === "mesh") {
      const v = p.vertices;
      const out = new Array<number>(v.length);
      const scratch: [number, number, number] = [0, 0, 0];
      for (let i = 0; i < v.length; i += 3) {
        scratch[0] = v[i];
        scratch[1] = v[i + 1];
        scratch[2] = v[i + 2];
        const w = affineApply(frameAt(scratch), scratch);
        out[i] = w[0];
        out[i + 1] = w[1];
        out[i + 2] = w[2];
      }
      return { ...p, vertices: out };
    }
    const fr = frameAt(p.center);
    if (isIdentityAffine(fr)) return p;
    const center = affineApply(fr, p.center) as [number, number, number];
    if (p.kind === "bumper") return { ...p, center, quat: fr.q };
    if (p.kind === "box") return { ...p, center, quat: fr.q };
    // Slab: fold its own yaw·pitch into the frame orientation.
    const local = quatMul(
      quatFromAxisAngle([0, 1, 0], p.yaw),
      quatFromAxisAngle([1, 0, 0], p.pitch),
    );
    return { ...p, center, quat: quatMul(fr.q, local) as Quat };
  });
}

/** Bumper collision sizing, world units: the mushroom prop at the
 * furniture scale (cap radius ≈ 0.18, height ≈ 0.41) with the hull a hair
 * inside the cap so the marble bounces off the visible rim. */
export const BUMPER_RADIUS = 0.16;
export const BUMPER_HEIGHT = 0.42;
export const BUMPER_RESTITUTION = 0.85;

/** Cylinder proxies for every bumper-flagged placement (positions are
 * already world-scaled by the stream). */
export function bumperProxies(
  placements: readonly { pos: readonly number[]; bumper?: boolean }[],
): BumperProxy[] {
  const out: BumperProxy[] = [];
  for (const p of placements) {
    if (!p.bumper) continue;
    out.push({
      kind: "bumper",
      center: [p.pos[0], p.pos[1], p.pos[2]],
      radius: BUMPER_RADIUS,
      height: BUMPER_HEIGHT,
      restitution: BUMPER_RESTITUTION,
    });
  }
  return out;
}

/** Channel cross-section half-profile (lateral, up) in world units at
 * the slalom scale: flat floor, gutter fillets, near-vertical walls.
 * MEASURED (T6, packed-kit audit 2026-08-09): wall inner face at 0.574,
 * rim at floor + 0.5 — the profile hugs those with a small clearance
 * (+0.05 lateral so the marble never pinches against the visible wall)
 * and a 0.12 flare cap over the rim as the solver margin for seam
 * kickers. The old guard reached up=1.1 — more than double the visible
 * rim — consuming tunnel/stack clearance and masking real edge exits. */
export const CHANNEL_PROFILE: readonly (readonly [number, number])[] = [
  [-0.66, 0.62],
  [-0.62, 0.50],
  [-0.60, 0.24],
  [-0.56, 0.10],
  [-0.50, 0.03],
  [-0.30, 0],
  [0.30, 0],
  [0.50, 0.03],
  [0.56, 0.10],
  [0.60, 0.24],
  [0.62, 0.50],
  [0.66, 0.62],
];

/** Sampling step along the centerline, world units. */
const STEP = 0.6;
/** Channel geometry at slalom scale (track walls ±0.35, height +0.5). */
const FLOOR_HALF_W = 0.38 * TRACK_SCALE;
const FLOOR_HALF_T = 0.15;
const WALL_HALF_W = 0.09 * TRACK_SCALE;
/** Walls span [floor − 0.25, floor + 0.55] world: real kit walls top out at
 * floor + 0.5; the slight over-height forgives curve seams, and the cap
 * matters — taller walls spear through the floor of a channel stacked
 * 1.0 above (drop-stack receivers barricaded their own upper deck). */
const WALL_HALF_H = 0.40;
const WALL_CENTER_LIFT = 0.15;
/** Samples steeper than this are drop shafts (cup + tube, no floor).
 * Only genuine plunges qualify — drop-stack dives are ty ≈ 1.0; steep
 * chutes reach ty ≈ 0.8 mid-cubic and MUST keep their floors (a 0.77
 * chute got cup-walled and threw the marble into the void). */
const VERTICAL_LIMIT = 0.93;

/** Floor slabs sink slightly so tilt ridges at sample seams stay below the
 * contact surface (poke ≈ halfLen·δ − SINK). */
const SINK = 0.035;
/** Max tangent turn per RING, radians — the crease-rounding law
 * (ribbonOf subdivision): sharp pitch/yaw V's between rings are kickers
 * that launch fast marbles. Distinct from arc STATION sampling, which
 * is boundary-error-bounded below (T3). */
const MAX_TURN = 0.12;
/** Arc tessellation error budget (T3), world units: the chord sagitta
 * at the OUTER channel boundary — what the player actually sees at the
 * offset walls — stays under this. (The old rule bounded center-frame
 * tangent rotation instead: tighter than needed on small radii, and no
 * guarantee at all had radii grown.) ~4% of the marble radius. */
const BOUNDARY_ERR = 0.008;

/** One sampled station along the path. */
interface Station {
  p: readonly [number, number, number];
  /** This part starts a separate channel, rather than continuing the
   * preceding part (a segment can contain two independent braid lanes). */
  breakBefore?: boolean;
  yaw: number;
  pitch: number;
  halfLen: number;
  /** Vertical drop sample (no floor; cup + guide tube instead). */
  vertical: boolean;
  /** Jump-gap sample (M11): no channel at all — ballistic flight. */
  gap: boolean;
  /** Diamond-window sample (M11): the slab composite replaces the ribbon
   * (two crossing one-sided branch meshes destabilized the solver). */
  diamond: boolean;
  /** Floor-pierce sample (T2): inside a hole's lip zone — the ribbon
   * stops; the aperture composite carries the deck around the bore. */
  hole: boolean;
  dy: number;
  /** Lateral profile WIDENING in world units (wide runs; funnels lerp
   * across their window): everything beyond the flat floor shifts
   * outward by this much, stretching the floor between — the kit widens
   * its channel by moving the walls out, not by scaling the section. */
  wShift: number;
}

/** Measured wide-channel widening (packed-kit audit 2026-08-09): inner
 * wall face 0.575 grid vs the standard 0.287 → each wall moves out
 * 0.288 grid = 0.576 world. (The old ×2.43 profile SCALE — from the
 * ±0.85 outer-bbox claim — put collision walls outside the visible
 * ±1.7 mesh entirely.) */
const WIDE_SHIFT = 0.576;
/** Profile points with |lat| beyond this are wall-side: they shift by
 * the station's wShift; the flat floor inside stays put. */
const PROFILE_FLAT = 0.31;

/** Measured drop/hole law (packed-kit audit 2026-08-09, world units):
 * the floor bore is ROUND, r 0.25 grid, in straight-hole AND both
 * end-hole flavors — square vs rounded differ only in cup cosmetics
 * (corner pockets beyond a marble's reach), so ONE authored shape serves
 * every variant. Cup wall inner face sits essentially at the cell edge,
 * rising the kit's 0.25-grid rim over the floor. */
const HOLE_R = 0.5;
const CUP_R = 1.0;
const CUP_H = 0.5;
/** Shaft bore guide: the hole radius plus marble clearance. */
const SHAFT_R = 0.55;

/** Junction composite walls follow the rim law (profile cap 0.62 over
 * the floor, foot sunk 0.10 below): span [floor−0.10, floor+0.62]. */
const JUNCTION_WALL_LIFT = 0.26;
const JUNCTION_WALL_HALF_H = 0.36;
/** Junction wall line: the kit's measured wall inner face (0.574) plus
 * lateral clearance — matches where the channel ribbon's wall now sits. */
const JUNCTION_HW = 0.66;
/** Branch-mouth wall line where a neighbor ribbon's walls take over. */
const JUNCTION_BR = 0.64;

/** Per-path-segment lateral WIDENING range [entry, exit] (world units)
 * for wide runs. Explicit per-window port widths (the def's authored
 * widthIn/widthOut — the bench passes them) win; otherwise widths are
 * derived from realized window families (wide windows are always
 * funnel-bracketed by construction; a funnel tapers toward its wide
 * neighbor). Undefined when the segment has no wide vocabulary. */
export function wideScalesOf(
  seg: {
    pieceFamilies: string[];
    widths?: readonly (
      | { widthIn?: string; widthOut?: string }
      | null
      | undefined
    )[];
  },
): Map<number, readonly [number, number]> | undefined {
  const fam = seg.pieceFamilies;
  const out = new Map<number, readonly [number, number]>();
  for (let i = 0; i < fam.length; i++) {
    const w = seg.widths?.[i];
    if (w && (w.widthIn === "wide" || w.widthOut === "wide")) {
      out.set(i, [
        w.widthIn === "wide" ? WIDE_SHIFT : 0,
        w.widthOut === "wide" ? WIDE_SHIFT : 0,
      ]);
    } else if (fam[i] === "wide") out.set(i, [WIDE_SHIFT, WIDE_SHIFT]);
    else if (fam[i] === "funnel") {
      if (fam[i + 1] === "wide") out.set(i, [0, WIDE_SHIFT]);
      else if (fam[i - 1] === "wide") out.set(i, [WIDE_SHIFT, 0]);
    }
  }
  return out.size > 0 ? out : undefined;
}

/** The segment fields proxy building needs (EndlessSegment satisfies it;
 * mirrors rail.ts RailSource plus the optional braid alt lane). */
export interface ProxySource {
  path: LoopPath;
  altPath?: LoopPath;
  pieceFamilies: string[];
  windowMods: (string | null)[];
  /** Explicit per-window port widths (def-authored) — the bench passes
   * them so an isolated funnel/wide window still gets its true section;
   * production omits them (families + neighbors carry the same truth). */
  widths?: readonly (
    | { widthIn?: string; widthOut?: string }
    | null
    | undefined
  )[];
}

/** Path-segment indices that are jump gaps — the collision proxies emit
 * no channel there (absence of geometry IS the gap). Lives with the
 * proxy law; rail.ts re-exports it for the drivers. */
export function gapSegmentsOf(
  seg: { windowMods: (string | null)[] },
): Set<number> {
  const out = new Set<number>();
  seg.windowMods.forEach((m, i) => {
    if (m === "jump" || m === "propeller") out.add(i);
  });
  return out;
}

/** The ONE channel-collision builder (T7 parity): production, the piece
 * bench, the tests, and the drive harness all call THIS — the segment's
 * main ribbon with gaps and wide scales resolved from the segment
 * itself, plus the braid alt lane's plain channel. Separately wired
 * diagnostic paths drew false conclusions (the bench dropped the wide
 * scales and under-reported five wide/funnel variants). */
export function segmentProxies(seg: ProxySource): CollisionProxy[] {
  const proxies = [
    ...channelProxies(seg.path, true, gapSegmentsOf(seg), wideScalesOf(seg)),
    // Braid alt LANE (M17): a plain channel between the junction
    // composites — its ribbon meets their open mouths at the ports.
    ...(seg.altPath ? channelProxies(seg.altPath, true) : []),
  ];
  // Pack independent strips into one mesh without joining their rings.
  // The native shim has a bounded mesh-handle budget per segment; two
  // braids plus jump/drop strips can otherwise exceed it. Each strip
  // retains its own vertices and triangles, so the gaps stay open.
  const meshes = proxies.filter((p): p is MeshProxy => p.kind === "mesh");
  if (meshes.length <= 1) return proxies;
  const joined: MeshProxy = { kind: "mesh", vertices: [], indices: [] };
  for (const mesh of meshes) {
    const offset = joined.vertices.length / 3;
    for (const v of mesh.vertices) joined.vertices.push(v);
    for (const i of mesh.indices) joined.indices.push(i + offset);
  }
  return [joined, ...proxies.filter((p) => p.kind !== "mesh")];
}

/** Channel slabs for one segment's world-space path. Two passes: sample
 * every part into stations, then emit — drop shafts need LOOKAHEAD (the
 * guide tube opens toward the receiver's exit heading). `gapSegments`
 * are path-segment indices that are jump gaps (rail.ts gapSegmentsOf):
 * they emit no channel, and the landing side gets a catch pan + funnel. */
export function channelProxies(
  path: LoopPath,
  useRibbon = true,
  gapSegments?: ReadonlySet<number>,
  wideScales?: ReadonlyMap<number, readonly [number, number]>,
): CollisionProxy[] {
  let previousEnd: readonly [number, number, number] | null = null;
  const sampleParts = (
    parts: LoopPath["segments"][number]["curve"],
    gap: boolean,
    into: Station[],
    diamond = false,
    wRange: readonly [number, number] | null = null,
    holes: readonly (readonly [number, number, number])[] = [],
  ): void => {
    const windowLen = wRange
      ? parts.reduce((acc, p) => acc + segLength(p), 0) || 1
      : 1;
    let travelled = 0;
    for (const part of parts) {
      const start = segPoint(part, 0);
      const disconnected = previousEnd !== null &&
        Math.hypot(
            start[0] - previousEnd[0],
            start[1] - previousEnd[1],
            start[2] - previousEnd[2],
          ) > 1e-4;
      previousEnd = segPoint(part, 1);
      const len = segLength(part);
      // Curvature-aware sampling: arcs bound the chord-sagitta error at
      // their OUTER channel boundary (inner boundary chords cut inward
      // by strictly less — the outer bound covers both); cubics (jogs)
      // sample finely; lines stay coarse. Every part contributes its own
      // stations, so part boundaries — inflections, curvature steps,
      // width changes — always land between dedicated samples.
      let steps: number;
      if (part.kind === "arc") {
        const outerR = part.radius +
          0.66 + (wRange ? Math.max(wRange[0], wRange[1]) : 0);
        const maxDelta = 2 *
          Math.acos(Math.max(0, 1 - BOUNDARY_ERR / outerR));
        steps = Math.max(
          Math.ceil(len / STEP),
          Math.ceil(Math.abs(part.a1 - part.a0) / maxDelta),
        );
      } else if (part.kind === "cubic") {
        steps = Math.max(1, Math.ceil(len / 0.3));
      } else {
        steps = Math.max(1, Math.ceil(len / STEP));
      }
      const stepLen = len / steps;
      const halfLen = Math.max(0.12, stepLen * 0.72);
      for (let i = 0; i < steps; i++) {
        const p = segPoint(part, (i + 0.5) / steps);
        const a = segPoint(part, i / steps);
        const b = segPoint(part, Math.min(1, (i + 1) / steps));
        const dx = b[0] - a[0];
        const dy = b[1] - a[1];
        const dz = b[2] - a[2];
        const horiz = Math.hypot(dx, dz);
        const total = Math.hypot(dx, dy, dz);
        if (total < 1e-9) continue;
        const vertical = !gap && Math.abs(dy / total) > VERTICAL_LIMIT;
        const wShift = wRange
          ? wRange[0] +
            (wRange[1] - wRange[0]) *
              Math.min(
                1,
                (travelled + len * ((i + 0.5) / steps)) / windowLen,
              )
          : 0;
        // Floor-pierce lip zone (T2): the ribbon must stop AT the bore,
        // not span it — samples whose center lands over/near the hole
        // are marked; the aperture composite carries the deck instead.
        const hole = !vertical && !gap &&
          holes.some((h) =>
            Math.hypot(p[0] - h[0], p[2] - h[2]) < HOLE_R + 0.15
          );
        into.push({
          p: [p[0], p[1], p[2]],
          ...(i === 0 && disconnected ? { breakBefore: true } : {}),
          yaw: vertical ? 0 : Math.atan2(dx, dz),
          pitch: vertical ? 0 : -Math.atan2(dy, horiz),
          halfLen,
          vertical,
          gap,
          diamond,
          hole,
          dy,
          wShift,
        });
      }
      travelled += len;
    }
  };
  const stations: Station[] = [];
  /** Floor-pierce apertures to emit (T2): center + travel yaw, resolved
   * after sampling from the nearest non-vertical station. */
  const apertures: { c: readonly [number, number, number]; yaw: number }[] = [];
  for (const [segIdx, seg] of path.segments.entries()) {
    const holes = (seg.holes ?? []) as readonly [number, number, number][];
    sampleParts(
      seg.curve,
      gapSegments?.has(segIdx) ?? false,
      stations,
      // Composite-carried windows: diamonds/spurs (alt), both braid
      // junctions (the fork carries the alt, the merge its own flag),
      // and grade crossings (host composite / quiet phantom) — the
      // ribbon stops at their ports. V3: a CONTINUATION chain's carrier
      // window is a PLAIN channel (the chain merely hangs there; the
      // real junction lives segments back) — its ribbon must stay.
      (seg.alt !== undefined && seg.alt.contBraid !== true) ||
        seg.braidMerge === true ||
        seg.junction !== undefined,
      wideScales?.get(segIdx) ?? null,
      holes,
    );
    for (const h of holes) apertures.push({ c: h, yaw: 0 });
  }
  // Resolve each aperture's travel yaw from the nearest rolling station.
  for (const a of apertures) {
    let best = Infinity;
    for (const st of stations) {
      if (st.vertical || st.gap) continue;
      const d = Math.hypot(st.p[0] - a.c[0], st.p[2] - a.c[2]);
      if (d < best) {
        best = d;
        a.yaw = st.yaw;
      }
    }
  }
  // Vertical stations inherit the approach yaw; their exit heading is the
  // next non-vertical station's yaw (the receiver's port).
  for (let i = 0; i < stations.length; i++) {
    if (!stations[i].vertical) continue;
    let j = i - 1;
    while (j >= 0 && stations[j].vertical) j--;
    stations[i] = { ...stations[i], yaw: j >= 0 ? stations[j].yaw : 0 };
  }
  // Bore-lip trim (T2, found by the metrics CI): a rolling station can
  // land arbitrarily close to a following dive column, and its ring
  // floor then overhangs the open bore — a shelf falling marbles clip.
  // Stations at dive-DECK height within the bore radius of the column
  // become lip stations: the ribbon stops there and the drop cup's
  // authored annulus floor carries the deck to the rim. (Stations at
  // receiver depth stay — the exit channel runs under the shaft.)
  for (const st of stations) {
    if (!st.vertical) continue;
    const top = st.p[1] + Math.abs(st.dy) / 2;
    for (let j = 0; j < stations.length; j++) {
      const s = stations[j];
      if (s.vertical || s.hole || s.gap) continue;
      if (Math.abs(s.p[1] - top) > 0.3) continue;
      if (Math.hypot(s.p[0] - st.p[0], s.p[2] - st.p[2]) < HOLE_R + 0.1) {
        stations[j] = { ...s, hole: true };
      }
    }
  }

  const out: CollisionProxy[] = [];
  // M11: the smooth ribbon trimesh is the default channel law (isolated
  // repro chain settled it: one-sided winding for the up×t layout + upV
  // cross-order fix). Slabs remain available for A/B comparison.
  const USE_RIBBON = useRibbon;
  let strip: Station[] = [];
  /** Whether the CURRENT strip's start should extrapolate to its true
   * port: yes at the path start, at segment boundaries, and after a jump
   * gap (the landing catches at its very edge); never out of a shaft. */
  let extendStart = true;
  /** Whether the CURRENT strip starts at a jump landing — its mouth rings
   * flare wide (the ribbon IS the catch funnel). */
  let flareStart = false;
  const flushStrip = (
    extendEnd: boolean,
    followingExtends: boolean,
    followingFlares = false,
  ) => {
    if (strip.length >= 2) {
      if (USE_RIBBON) {
        // Extend to the true strip ends (segment boundaries share ring
        // positions with the neighbor segment → seamless; jump lips run
        // to the gap edge), but never toward a drop shaft (that would
        // overhang the mouth).
        out.push(ribbonOf(strip, extendStart, extendEnd, flareStart));
      } else {
        strip.forEach((st, idx) =>
          emitSlabs(st, out, !extendEnd && idx === strip.length - 1)
        );
      }
    }
    strip = [];
    extendStart = followingExtends;
    flareStart = followingFlares;
  };
  for (let i = 0; i < stations.length; i++) {
    const st = stations[i];
    // Independent lanes must have independent meshes. Connecting their
    // last/first rings creates an invisible floor and wall across the
    // space between them, sometimes cutting through another live track.
    if (st.breakBefore) flushStrip(true, true);
    const [px, , pz] = st.p;
    if (st.diamond) {
      // Diamond window: the ribbon stops at both ports; the slab
      // composite (emitted below, from the segment's own geometry)
      // carries the marble across.
      flushStrip(true, true);
      let k = i;
      while (k < stations.length && stations[k].diamond) k++;
      i = k - 1;
      continue;
    }
    if (st.hole) {
      // Floor-pierce (T2): the ribbon stops short of the bore on both
      // sides — NO extension toward the hole (an extrapolated tongue
      // over the open bore would catch falling marbles). The aperture
      // composite (emitted below) carries the deck around the hole.
      flushStrip(false, false);
      let k = i;
      while (k < stations.length && stations[k].hole) k++;
      i = k - 1;
      continue;
    }
    if (st.gap) {
      // JUMP gap: the lip ribbon runs to the gap edge, then NOTHING — the
      // marble flies. The landing side gets a catch pan reaching back
      // under the flight path plus funnel walls, so undershoots land and
      // roll on instead of clipping the landing's leading face.
      flushStrip(true, true, true);
      let k = i;
      while (k < stations.length && stations[k].gap) k++;
      if (k < stations.length && !stations[k].vertical) {
        emitCatch(stations[k], out);
      }
      i = k - 1;
      continue;
    }
    if (st.vertical) {
      flushStrip(false, false);
      // One CUP PER CONTIGUOUS VERTICAL RUN (per-station emission stacked
      // overlapping cups that wedged the marble — found the hard way).
      // Frame: approach yaw; open side: receiver exit yaw.
      let k = i;
      while (k < stations.length && stations[k].vertical) k++;
      const group = stations.slice(i, k);
      const topY = Math.max(...group.map((g) => g.p[1] + Math.abs(g.dy) / 2));
      const botY = Math.min(...group.map((g) => g.p[1] - Math.abs(g.dy) / 2));
      const exitYaw = k < stations.length ? stations[k].yaw : st.yaw;
      const lx = Math.cos(st.yaw);
      const lz = -Math.sin(st.yaw);
      const fwdX = Math.sin(st.yaw);
      const fwdZ = Math.cos(st.yaw);
      // Authored drop shape (T2/T5, measured): TOP CUP — annulus floor
      // slabs around the open bore (the deck the marble circles before
      // dropping; the bore itself stays open) + octagon cup walls at the
      // measured cell-edge radius with the ENTRY port arch open. The
      // square opening between the four annulus slabs inscribes the
      // round bore; its corner triangles read open where the mesh has
      // floor — beyond a rolling marble's reach from the port.
      const deckY = topY - FLOOR_HALF_T + 0.02; // hair above ribbon sheets
      for (
        const [ox, oz, hx, hz] of [
          [fwdX * 0.75, fwdZ * 0.75, CUP_R + 0.05, 0.25],
          [-fwdX * 0.75, -fwdZ * 0.75, CUP_R + 0.05, 0.25],
          [lx * 0.75, lz * 0.75, 0.25, HOLE_R],
          [-lx * 0.75, -lz * 0.75, 0.25, HOLE_R],
        ] as const
      ) {
        out.push({
          kind: "slab",
          center: [px + ox, deckY, pz + oz],
          half: [hx, FLOOR_HALF_T, hz],
          yaw: st.yaw,
          pitch: 0,
        });
      }
      octaRing(
        out,
        px,
        pz,
        st.yaw,
        CUP_R,
        topY,
        topY + CUP_H,
        st.yaw + Math.PI,
      );
      // SHAFT: full octagonal bore guide between the decks (the hole is
      // round in every flavor — the old square tube was not).
      octaRing(out, px, pz, st.yaw, SHAFT_R, botY + 0.45, topY - 0.02, null);
      // RECEIVER CUP: octagon walls at the measured radius, EXIT port
      // arch open (l/r variants turn — the frame is the exit yaw).
      octaRing(out, px, pz, exitYaw, CUP_R, botY, botY + CUP_H, exitYaw);
      // Bottom pan: the full cup cross-section at receiver-floor level.
      // Its top sits a hair ABOVE the receiver ribbon sheet — arriving
      // even 0.03 below a one-sided mesh means catching its edge forever.
      out.push({
        kind: "slab",
        center: [px, botY - FLOOR_HALF_T + 0.03, pz],
        half: [CUP_R + 0.05, FLOOR_HALF_T, CUP_R + 0.05],
        yaw: st.yaw,
        pitch: 0,
      });
      i = k - 1; // consume the group
      continue;
    }
    strip.push(st);
  }
  flushStrip(true, true);

  // Diamond windows (M11 branch respawns): one slab composite per window
  // — a wide flat deck, outer flare/straight walls, and a center wedge
  // divider, mirroring the real split-double geometry. (Two overlapping
  // one-sided branch RIBBONS were tried first: their surfaces cross at
  // the split and the wedged marble destabilized the solver.)
  // Takeable spur windows carry the alt marker `spur` and get their own
  // composite instead: the branch deck with the hole OPEN.
  for (const seg of path.segments) {
    if (seg.alt?.spur) emitSpur(seg, out);
    else if (seg.alt?.braid) continue; // braid junctions emitted below
    else if (seg.alt) emitDiamond(seg, out);
    else if (seg.junction === "cross") emitCross(seg, out);
    // junction "quiet" (the crossing's second visit): nothing — the
    // deck belongs to the mesh visit's composite.
  }
  // Floor-pierce apertures (T2): deck lips + walls around each open bore.
  for (const a of apertures) emitAperture(a.c, a.yaw, out);
  // Braid junctions (M17): fork (alt.braid) and merge (braidMerge) share
  // ONE composite shape — a straight junction deck whose branch mouth is
  // OPEN on the alt side (the spur junction without the hole/board/cap).
  // The alt LANE between them is a plain channel — the stream emits
  // channelProxies over EndlessSegment.altPath separately, its ribbon
  // meeting these mouths at the branch ports. The branch side comes from
  // sampling the fork's alt chain just past each junction (the lane sits
  // a full track cell out — the sign is unambiguous).
  // M23: a segment may carry TWO chains (a continuation resolving plus a
  // fresh fork). Chains and merge windows pair IN PATH ORDER — each
  // chain that merges here consumes the next braidMerge window.
  const chainSegs = path.segments.filter((s) => s.alt?.braid);
  const mergeSegs = path.segments.filter((s) => s.braidMerge);
  let mi = 0;
  for (const cs of chainSegs) {
    const total = cs.alt!.length;
    const near = pointAt(cs.alt!.curve, Math.min(2.5, total / 2));
    const far = pointAt(cs.alt!.curve, Math.max(total - 2.5, total / 2));
    // V3: a CONTINUATION chain enters at the segment boundary — its
    // entry host is a plain straight, not a junction; cutting a fork
    // mouth into it would open a hole in an ordinary channel wall.
    if (!cs.alt!.contBraid) emitBraidJunction(cs, near, out);
    // Likewise an OPEN chain exits at the boundary — no merge there.
    if (!cs.alt!.openBraid) {
      const ms = mergeSegs[mi++];
      if (ms) emitBraidJunction(ms, far, out);
    }
  }
  return out;
}

/** Point at arc length `s` along a curve chain (clamped to its ends). */
function pointAt(
  curve: readonly CurveSeg[],
  s: number,
): [number, number, number] {
  let d = Math.max(0, s);
  for (const part of curve) {
    const len = segLength(part);
    if (d <= len) {
      const p = segPoint(part, len === 0 ? 0 : d / len);
      return [p[0], p[1], p[2]];
    }
    d -= len;
  }
  const p = segPoint(curve[curve.length - 1], 1);
  return [p[0], p[1], p[2]];
}

/** Slab composite for a braid junction window (fork AND merge — the
 * shape is flow-symmetric). World geometry (the 1-cell host window is
 * straight and flat): the split-junction deck with the branch mouth open
 * toward `ref` (a point on the alt lane beside this junction), mouth
 * side walls out to the branch PORT at the cell face — where the alt
 * lane's own ribbon takes over — and wall stubs framing the mouth on the
 * main line. Deck top a hair above the neighboring ribbon sheets (the
 * spur/diamond law: arriving under a one-sided mesh edge means catching
 * it forever). */
function emitBraidJunction(
  seg: LoopPath["segments"][number],
  ref: readonly [number, number, number],
  out: CollisionProxy[],
): void {
  const entry = segPoint(seg.curve[0], 0);
  const lastPart = seg.curve[seg.curve.length - 1];
  const exit = segPoint(lastPart, 1);
  const dx = exit[0] - entry[0];
  const dz = exit[2] - entry[2];
  const L = Math.hypot(dx, dz);
  if (L < 1e-6) return;
  const yaw = Math.atan2(dx, dz);
  const fwd: [number, number] = [dx / L, dz / L];
  let lat: [number, number] = [fwd[1], -fwd[0]];
  const floorY = entry[1];
  // Orient +side toward the branch.
  const mx = entry[0] + fwd[0] * (L / 2);
  const mz = entry[2] + fwd[1] * (L / 2);
  if ((ref[0] - mx) * lat[0] + (ref[2] - mz) * lat[1] < 0) {
    lat = [-lat[0], -lat[1]];
  }
  const branchYaw = Math.atan2(lat[0], lat[1]);
  const at = (
    along: number,
    side: number,
  ): [number, number] => [
    entry[0] + fwd[0] * along + lat[0] * side,
    entry[2] + fwd[1] * along + lat[1] * side,
  ];
  const wall = (
    a: readonly [number, number],
    b: readonly [number, number],
  ): void => {
    const [ax, az] = at(a[0], a[1]);
    const [bx, bz] = at(b[0], b[1]);
    const wx = bx - ax;
    const wz = bz - az;
    const wlen = Math.hypot(wx, wz);
    out.push({
      kind: "slab",
      center: [(ax + bx) / 2, floorY + JUNCTION_WALL_LIFT, (az + bz) / 2],
      half: [0.09, JUNCTION_WALL_HALF_H, wlen / 2],
      yaw: Math.atan2(wx, wz),
      pitch: 0,
    });
  };

  const HW = JUNCTION_HW; // main wall line (measured inner face + clearance)
  const BR = JUNCTION_BR; // branch wall line (ribbon walls take over)
  const EDGE = L / 2 + 0.05; // branch port at the cell face (+ seal)
  const deckY = floorY - FLOOR_HALF_T + 0.02;

  // Junction deck (main lane), reaching to both ports.
  {
    const [cx, cz] = at(L / 2, 0);
    out.push({
      kind: "slab",
      center: [cx, deckY, cz],
      half: [HW, FLOOR_HALF_T, L / 2 + 0.05],
      yaw,
      pitch: 0,
    });
  }
  // Branch mouth deck out to the port.
  {
    const s0 = HW - 0.15;
    const [cx, cz] = at(L / 2, (s0 + EDGE) / 2);
    out.push({
      kind: "slab",
      center: [cx, deckY, cz],
      half: [BR, FLOOR_HALF_T, (EDGE - s0) / 2],
      yaw: branchYaw,
      pitch: 0,
    });
  }
  // Main-lane walls: solid on the far side; mouth-framing stubs on the
  // branch side (the neighbor ribbons' walls stop at the ports).
  wall([-0.05, -HW], [L + 0.05, -HW]);
  wall([-0.05, HW], [L / 2 - BR, HW]);
  wall([L / 2 + BR, HW], [L + 0.05, HW]);
  // Mouth side walls, junction wall line out to the branch port (the alt
  // ribbon's own gutter walls continue from there).
  wall([L / 2 - BR, HW], [L / 2 - BR, EDGE]);
  wall([L / 2 + BR, HW], [L / 2 + BR, EDGE]);
}

/** Authored 4-way grade-crossing composite (T2, measured): a flat cell
 * deck reaching all four ports plus a SOLID corner post per quadrant
 * (the kit's corner blocks run square from the wall line to the cell
 * corner — thin L walls left the posts' outer verts 0.29 uncovered) —
 * every port arch open. Replaces the single-route ribbon (which left
 * the perpendicular arm floorless in isolation, and in production two
 * overlapping same-height ribbons walled each other's route off). */
function emitCross(
  seg: LoopPath["segments"][number],
  out: CollisionProxy[],
): void {
  const entry = segPoint(seg.curve[0], 0);
  const lastPart = seg.curve[seg.curve.length - 1];
  const exit = segPoint(lastPart, 1);
  const dx = exit[0] - entry[0];
  const dz = exit[2] - entry[2];
  const L = Math.hypot(dx, dz);
  if (L < 1e-6) return;
  const yaw = Math.atan2(dx, dz);
  const fwd: [number, number] = [dx / L, dz / L];
  const lat: [number, number] = [fwd[1], -fwd[0]];
  const floorY = entry[1];
  const cx = entry[0] + fwd[0] * (L / 2);
  const cz = entry[2] + fwd[1] * (L / 2);
  const E = L / 2 + 0.05; // cell face + seal
  // Deck: the whole cell, top a hair above the neighbor ribbon sheets.
  out.push({
    kind: "slab",
    center: [cx, floorY - FLOOR_HALF_T + 0.02, cz],
    half: [E, FLOOR_HALF_T, E],
    yaw,
    pitch: 0,
  });
  // Corner posts: solid squares from the wall line (kit inner face
  // 0.574 + clearance) to the cell corner, one per quadrant.
  const P = 0.62;
  const half = (E - P) / 2;
  const mid = (P + E) / 2;
  for (const sa of [-1, 1]) {
    for (const sl of [-1, 1]) {
      out.push({
        kind: "slab",
        center: [
          cx + fwd[0] * sa * mid + lat[0] * sl * mid,
          floorY + JUNCTION_WALL_LIFT,
          cz + fwd[1] * sa * mid + lat[1] * sl * mid,
        ],
        half: [half, JUNCTION_WALL_HALF_H, half],
        yaw,
        pitch: 0,
      });
    }
  }
}

/** Slab composite for a split–merge diamond window. World geometry (the
 * window is straight and flat): branches at lateral ±2, diverge/merge
 * cubics 4 world long each, N straights between; wall lines at the kit's
 * measured inner faces (outer = branch + 0.574, wedge = branch − 0.574,
 * + clearance — the old ±0.95 line overshot the sculpted deck and wedge
 * by ~0.4 each side). Two-sided box slabs — crossings and overlaps are
 * harmless. */
function emitDiamond(
  seg: LoopPath["segments"][number],
  out: CollisionProxy[],
): void {
  const entry = segPoint(seg.curve[0], 0);
  const last = seg.curve[seg.curve.length - 1];
  const exit = segPoint(last, 1);
  const dx = exit[0] - entry[0];
  const dz = exit[2] - entry[2];
  const L = Math.hypot(dx, dz);
  if (L < 1e-6) return;
  const yaw = Math.atan2(dx, dz);
  const fwd: [number, number] = [dx / L, dz / L];
  const lat: [number, number] = [fwd[1], -fwd[0]];
  const floorY = entry[1];
  const at = (
    along: number,
    side: number,
  ): [number, number, number] => [
    entry[0] + fwd[0] * along + lat[0] * side,
    floorY,
    entry[2] + fwd[1] * along + lat[1] * side,
  ];
  const wall = (
    a: readonly [number, number],
    b: readonly [number, number],
  ): void => {
    // Wall slab from (along, side) a → b at deck level.
    const ax = entry[0] + fwd[0] * a[0] + lat[0] * a[1];
    const az = entry[2] + fwd[1] * a[0] + lat[1] * a[1];
    const bx = entry[0] + fwd[0] * b[0] + lat[0] * b[1];
    const bz = entry[2] + fwd[1] * b[0] + lat[1] * b[1];
    const wx = bx - ax;
    const wz = bz - az;
    const wlen = Math.hypot(wx, wz);
    out.push({
      kind: "slab",
      center: [(ax + bx) / 2, floorY + JUNCTION_WALL_LIFT, (az + bz) / 2],
      half: [0.09, JUNCTION_WALL_HALF_H, wlen / 2],
      yaw: Math.atan2(wx, wz),
      pitch: 0,
    });
  };

  const C = 4.0; // diverge/merge cubic length (2 track cells)
  const B = 2.0; // branch lateral offset
  const HW = JUNCTION_HW; // wall line (measured inner face + clearance)
  // Deck: the full 3-column footprint, top a hair above the neighboring
  // ribbon sheets so entry/exit never catch a mesh edge.
  const mid = at(L / 2, 0);
  out.push({
    kind: "slab",
    center: [mid[0], floorY - FLOOR_HALF_T + 0.02, mid[2]],
    half: [B + HW, FLOOR_HALF_T, L / 2 + 0.05],
    yaw,
    pitch: 0,
  });
  for (const side of [-1, 1]) {
    // Outer walls: entry flare, branch straight, merge flare.
    wall([0, side * HW], [C, side * (B + HW)]);
    wall([C, side * (B + HW)], [L - C, side * (B + HW)]);
    wall([L - C, side * (B + HW)], [L, side * HW]);
    // Center wedge: tip where the inner channel edges cross (≈ half the
    // cubic), widening to the island between the branch straights.
    wall([C / 2, 0], [C, side * (B - HW)]);
    wall([C, side * (B - HW)], [L - C, side * (B - HW)]);
    wall([L - C, side * (B - HW)], [L - C / 2, 0]);
  }
}

/** Slab composite for a takeable spur window (endless respawn variety).
 * World geometry (the 1-cell host window is straight and flat): the
 * split-junction deck with the branch mouth open on one side, the
 * diving-board floor running out to the HOLE LIP and then STOPPING (no
 * floor over the hole — the fall is real physics), branch side walls out
 * to a cap wall past the hole that reaches down into the drop shaft (a
 * rolling marble can never clear it: it leaves the lip falling, so it
 * always meets the cap below its top and drops in), a near shaft guide
 * under the floor lip, and a catch pan a hair above the receiver's
 * ribbon floor Δ exactly 1.0 track (2.0 world) below — the receiver is
 * the segment's OWN line passing back under the branch cell by
 * construction, so every fall lands on track. */
function emitSpur(
  seg: LoopPath["segments"][number],
  out: CollisionProxy[],
): void {
  if (!seg.alt) return;
  const entry = segPoint(seg.curve[0], 0);
  const lastPart = seg.curve[seg.curve.length - 1];
  const exit = segPoint(lastPart, 1);
  const dx = exit[0] - entry[0];
  const dz = exit[2] - entry[2];
  const L = Math.hypot(dx, dz);
  if (L < 1e-6) return;
  const yaw = Math.atan2(dx, dz);
  const fwd: [number, number] = [dx / L, dz / L];
  let lat: [number, number] = [fwd[1], -fwd[0]];
  const floorY = entry[1];
  // Orient +side toward the branch: the alt centerline ends out over the
  // hole, so its endpoint picks the sign.
  const altLast = seg.alt.curve[seg.alt.curve.length - 1];
  const tip = segPoint(altLast, 1);
  const mx = entry[0] + fwd[0] * (L / 2);
  const mz = entry[2] + fwd[1] * (L / 2);
  if ((tip[0] - mx) * lat[0] + (tip[2] - mz) * lat[1] < 0) {
    lat = [-lat[0], -lat[1]];
  }
  const branchYaw = Math.atan2(lat[0], lat[1]);
  const at = (
    along: number,
    side: number,
  ): [number, number] => [
    entry[0] + fwd[0] * along + lat[0] * side,
    entry[2] + fwd[1] * along + lat[1] * side,
  ];
  const wall = (
    a: readonly [number, number],
    b: readonly [number, number],
  ): void => {
    const [ax, az] = at(a[0], a[1]);
    const [bx, bz] = at(b[0], b[1]);
    const wx = bx - ax;
    const wz = bz - az;
    const wlen = Math.hypot(wx, wz);
    out.push({
      kind: "slab",
      center: [(ax + bx) / 2, floorY + JUNCTION_WALL_LIFT, (az + bz) / 2],
      half: [0.09, JUNCTION_WALL_HALF_H, wlen / 2],
      yaw: Math.atan2(wx, wz),
      pitch: 0,
    });
  };

  const HW = JUNCTION_HW; // main wall line (measured inner face + clearance)
  const BR = JUNCTION_BR; // branch wall line (ribbon walls take over)
  const HOLE = TRACK_SCALE; // hole center: the branch cell center
  const NEAR = HOLE - 0.55; // the floor lip — the hole opens here
  const CAP = HOLE + 0.6; // cap wall past the hole
  const recY = floorY - TRACK_SCALE; // Δ 1.0 track below (bridge law)
  const deckY = floorY - FLOOR_HALF_T + 0.02; // hair above ribbon sheets

  // Junction deck (main lane), reaching to both ports.
  {
    const [cx, cz] = at(L / 2, 0);
    out.push({
      kind: "slab",
      center: [cx, deckY, cz],
      half: [HW, FLOOR_HALF_T, L / 2 + 0.05],
      yaw,
      pitch: 0,
    });
  }
  // Diving-board floor: junction edge out to the hole lip. NOTHING past
  // the lip — the opening IS the set-piece.
  {
    const s0 = HW - 0.15;
    const [cx, cz] = at(L / 2, (s0 + NEAR) / 2);
    out.push({
      kind: "slab",
      center: [cx, deckY, cz],
      half: [BR, FLOOR_HALF_T, (NEAR - s0) / 2],
      yaw: branchYaw,
      pitch: 0,
    });
  }
  // Main-lane walls: solid on the far side; mouth-framing stubs on the
  // branch side (the neighbor ribbons' walls stop at the ports).
  wall([-0.05, -HW], [L + 0.05, -HW]);
  wall([-0.05, HW], [L / 2 - BR, HW]);
  wall([L / 2 + BR, HW], [L + 0.05, HW]);
  // Branch side walls, junction mouth out past the hole to the cap.
  wall([L / 2 - BR, HW], [L / 2 - BR, CAP]);
  wall([L / 2 + BR, HW], [L / 2 + BR, CAP]);
  // Cap wall: crosses the branch past the hole and reaches DOWN into the
  // shaft (top at wall height, bottom just above the receiver's own wall
  // tops — the marble cannot slip under, and the receiver channel stays
  // clear beneath it).
  {
    const top = floorY + 0.62;
    const bot = recY + 0.75;
    const [cx, cz] = at(L / 2, CAP);
    out.push({
      kind: "slab",
      center: [cx, (top + bot) / 2, cz],
      half: [BR, (top - bot) / 2, 0.09],
      yaw: branchYaw,
      pitch: 0,
    });
  }
  // Near shaft guide under the floor lip: a cap-side bounce-back drops
  // straight down inside the shaft instead of drifting back under the
  // board and over the receiver's near wall.
  {
    const top = floorY - 0.2;
    const bot = recY + 0.75;
    const [cx, cz] = at(L / 2, NEAR);
    out.push({
      kind: "slab",
      center: [cx, (top + bot) / 2, cz],
      half: [BR, (top - bot) / 2, 0.09],
      yaw: branchYaw,
      pitch: 0,
    });
  }
  // Catch pan under the hole, a hair ABOVE the receiver ribbon sheet
  // (arriving below a one-sided mesh means catching its edge forever —
  // the drop-shaft law).
  {
    const [cx, cz] = at(L / 2, HOLE);
    out.push({
      kind: "slab",
      center: [cx, recY - FLOOR_HALF_T + 0.03, cz],
      half: [0.75, FLOOR_HALF_T, 0.75],
      yaw,
      pitch: 0,
    });
  }
}

/** Catch pan at a jump landing, reaching back under the flight path. Its
 * top sits a hair ABOVE the landing ribbon sheet (arriving below a
 * one-sided mesh means catching its edge forever). Lateral catching is
 * the RIBBON MOUTH's job: the landing strip's first rings flare wide
 * (ribbonOf flareStart), so off-center landings meet flat floor and the
 * converging gutters shepherd the marble to center — funnel wall slabs
 * were tried twice and either clipped shallow flights from above or made
 * wedge pockets at their tips. */
function emitCatch(landing: Station, out: CollisionProxy[]): void {
  const fwdX = Math.sin(landing.yaw);
  const fwdZ = Math.cos(landing.yaw);
  const floorY = landing.p[1];
  const cx = landing.p[0] - fwdX * 1.0;
  const cz = landing.p[2] - fwdZ * 1.0;
  out.push({
    kind: "slab",
    center: [cx, floorY - FLOOR_HALF_T + 0.03, cz],
    half: [1.1, FLOOR_HALF_T, 1.05],
    yaw: landing.yaw,
    pitch: 0,
  });
}

/** Octagonal wall ring (the measured drop-cup law): slab walls on each
 * side of an octagon of INNER radius `r` (slabs pushed outward by their
 * half-thickness), spanning [yLo, yHi]. `open` is the outward yaw of an
 * open port side — it must align with a side normal (pass the matching
 * frame yaw): that side is skipped and its two neighbours are CLIPPED
 * back to the kit's port half-width so no wall intrudes into the arch.
 * `null` = fully closed ring. */
function octaRing(
  out: CollisionProxy[],
  cx: number,
  cz: number,
  yaw: number,
  r: number,
  yLo: number,
  yHi: number,
  open: number | null,
): void {
  /** Kit port arch half-width (measured 0.574) + marble clearance. */
  const PORT_HALF_W = 0.62;
  const rv = r / Math.cos(Math.PI / 8);
  const openDir = open === null
    ? null
    : ([Math.sin(open), Math.cos(open)] as const);
  for (let k = 0; k < 8; k++) {
    const theta = yaw + k * (Math.PI / 4);
    const a0 = theta - Math.PI / 8;
    const a1 = theta + Math.PI / 8;
    let ax = cx + Math.sin(a0) * rv;
    let az = cz + Math.cos(a0) * rv;
    let bx = cx + Math.sin(a1) * rv;
    let bz = cz + Math.cos(a1) * rv;
    if (openDir) {
      const nd = Math.sin(theta) * openDir[0] + Math.cos(theta) * openDir[1];
      if (nd > 0.99) continue; // the open port side itself
      if (nd > 0.5) {
        // Port neighbour: pull its arch-side endpoint back to the port
        // edge (lateral distance from the open axis).
        const latOf = (ex: number, ez: number) =>
          (ex - cx) * openDir[1] - (ez - cz) * openDir[0];
        const la = latOf(ax, az);
        const lb = latOf(bx, bz);
        const target = Math.sign(la + lb) * PORT_HALF_W;
        if (Math.abs(la) < PORT_HALF_W) {
          const t = (target - la) / (lb - la);
          if (t > 0 && t < 1) {
            ax += (bx - ax) * t;
            az += (bz - az) * t;
          }
        } else if (Math.abs(lb) < PORT_HALF_W) {
          const t = (target - lb) / (la - lb);
          if (t > 0 && t < 1) {
            bx += (ax - bx) * t;
            bz += (az - bz) * t;
          }
        }
      }
    }
    const wx = bx - ax;
    const wz = bz - az;
    const wlen = Math.hypot(wx, wz);
    if (wlen < 0.05) continue;
    out.push({
      kind: "slab",
      center: [
        (ax + bx) / 2 + Math.sin(theta) * 0.09,
        (yLo + yHi) / 2,
        (az + bz) / 2 + Math.cos(theta) * 0.09,
      ],
      half: [0.09, (yHi - yLo) / 2, wlen / 2],
      yaw: Math.atan2(wx, wz),
      pitch: 0,
    });
  }
}

/** Deck composite around an open floor bore (T2 — straight-hole): lip
 * floor slabs up/downstream overlapping the stopped ribbon, thin side
 * slivers between bore and wall (the render-distance CI measured the
 * kit's bore collar 0.24 from collision without them), side walls
 * spanning the pierced window, and a bore guide ring just under the
 * deck so a lip clip drops clean instead of drifting under the deck.
 * NOTHING below the guide: hole placement guarantees track underneath
 * by construction (hole semantics, USER RULE), and the fall lands on
 * that channel's own one-sided ribbon from above. */
function emitAperture(
  c: readonly [number, number, number],
  yaw: number,
  out: CollisionProxy[],
): void {
  const fwdX = Math.sin(yaw);
  const fwdZ = Math.cos(yaw);
  const lx = Math.cos(yaw);
  const lz = -Math.sin(yaw);
  const deckY = c[1] - FLOOR_HALF_T + 0.02; // hair above ribbon sheets
  const off = FLOOR_HALF_W + WALL_HALF_W - 0.04; // emitSlabs wall line
  for (const s of [-1, 1] as const) {
    out.push({
      kind: "slab",
      center: [
        c[0] + fwdX * s * (HOLE_R + 0.55),
        deckY,
        c[2] + fwdZ * s * (HOLE_R + 0.55),
      ],
      half: [FLOOR_HALF_W, FLOOR_HALF_T, 0.55],
      yaw,
      pitch: 0,
    });
    out.push({
      kind: "slab",
      center: [
        c[0] + lx * s * off,
        c[1] + WALL_CENTER_LIFT,
        c[2] + lz * s * off,
      ],
      half: [WALL_HALF_W, WALL_HALF_H, HOLE_R + 1.1],
      yaw,
      pitch: 0,
    });
    // Side sliver deck between bore and wall: only ~0.11 wide, but the
    // kit's floor is there and the bore collar otherwise floats 0.24
    // from any collision.
    out.push({
      kind: "slab",
      center: [
        c[0] + lx * s * (HOLE_R + 0.06),
        deckY,
        c[2] + lz * s * (HOLE_R + 0.06),
      ],
      half: [0.11, FLOOR_HALF_T, HOLE_R + 0.12],
      yaw,
      pitch: 0,
    });
  }
  const g = HOLE_R + 0.09;
  for (
    const [ox, oz, hx, hz] of [
      [fwdX * g, fwdZ * g, 0.55, 0.09],
      [-fwdX * g, -fwdZ * g, 0.55, 0.09],
      [lx * g, lz * g, 0.09, 0.55],
      [-lx * g, -lz * g, 0.09, 0.55],
    ] as const
  ) {
    out.push({
      kind: "slab",
      center: [c[0] + ox, c[1] - 0.55, c[2] + oz],
      half: [hx, 0.4, hz],
      yaw,
      pitch: 0,
    });
  }
}

/** Slab law (proven, M10): floor follows slope; walls are vertical. The
 * floor slab approaching a drop shaft pulls back so it doesn't overhang
 * the mouth. */
function emitSlabs(
  st: Station,
  out: CollisionProxy[],
  trimForward = false,
): void {
  const [px, py, pz] = st.p;
  const trim = trimForward ? 0.3 : 0;
  const tx = Math.sin(st.yaw);
  const tz = Math.cos(st.yaw);
  out.push({
    kind: "slab",
    center: [px - tx * trim, py - FLOOR_HALF_T - SINK, pz - tz * trim],
    half: [FLOOR_HALF_W, FLOOR_HALF_T, Math.max(0.1, st.halfLen - trim)],
    yaw: st.yaw,
    pitch: st.pitch,
  });
  const lx = Math.cos(st.yaw);
  const lz = -Math.sin(st.yaw);
  const off = FLOOR_HALF_W + WALL_HALF_W - 0.04;
  for (const side of [-1, 1]) {
    out.push({
      kind: "slab",
      center: [
        px + lx * off * side,
        py + WALL_CENTER_LIFT,
        pz + lz * off * side,
      ],
      half: [WALL_HALF_W, WALL_HALF_H, st.halfLen + 0.06],
      yaw: st.yaw,
      pitch: 0,
    });
  }
}

/** Extrude the channel PROFILE along a strip of stations into one mesh
 * with shared ring vertices (C0-continuous — no seams to catch on).
 * `flareStart` widens the first rings laterally (×1.35 at the mouth,
 * blending to ×1 over ~2 world units): the jump-landing catch mouth —
 * off-center ballistic landings meet flat floor instead of a gutter
 * edge, and the converging gutters center the marble as it rolls. */
function ribbonOf(
  strip: readonly Station[],
  extendStart: boolean,
  extendEnd: boolean,
  flareStart = false,
): MeshProxy {
  const vertices: number[] = [];
  const indices: number[] = [];
  const N = CHANNEL_PROFILE.length;
  let last: Station | null = null;
  const rings: Station[] = [];
  // Boundary rings: stations are mid-samples, so the strip would stop
  // half a step short of the port on each side — the gap that dropped
  // marbles at every segment boundary. Extrapolate to the true ends
  // (+0.05 overlap so welded neighbors seal).
  const extrapolate = (st: Station, dir: 1 | -1): Station => {
    const stepLen = st.halfLen / 0.72;
    const d = (stepLen / 2 + 0.05) * dir;
    const ty = Math.sin(-st.pitch);
    const th = Math.cos(st.pitch);
    return {
      ...st,
      p: [
        st.p[0] + Math.sin(st.yaw) * th * d,
        st.p[1] + ty * d,
        st.p[2] + Math.cos(st.yaw) * th * d,
      ],
    };
  };
  const raw = [
    ...(extendStart ? [extrapolate(strip[0], -1)] : []),
    ...strip,
    ...(extendEnd ? [extrapolate(strip[strip.length - 1], 1)] : []),
  ];
  // Valley/crest rounding: a sharp pitch (or yaw) crease between rings is
  // a KICKER — a marble arriving fast reflects off the V and launches
  // over the wall (chute valleys threw marbles laterally; slabs had
  // incidentally rounded these with overlaps). Subdivide any step whose
  // frame turns faster than MAX_TURN into blended rings.
  const source: Station[] = [];
  const angleDelta = (a: number, b: number): number => {
    let d = b - a;
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    return d;
  };
  for (let i = 0; i < raw.length; i++) {
    if (i === 0) {
      source.push(raw[i]);
      continue;
    }
    const a = raw[i - 1];
    const b = raw[i];
    const dPitch = b.pitch - a.pitch;
    const dYaw = angleDelta(a.yaw, b.yaw);
    const steps = Math.max(
      1,
      Math.ceil(Math.max(Math.abs(dPitch), Math.abs(dYaw)) / MAX_TURN),
    );
    for (let j = 1; j <= steps; j++) {
      const t = j / steps;
      source.push({
        ...b,
        p: [
          a.p[0] + (b.p[0] - a.p[0]) * t,
          a.p[1] + (b.p[1] - a.p[1]) * t,
          a.p[2] + (b.p[2] - a.p[2]) * t,
        ],
        yaw: a.yaw + dYaw * t,
        pitch: a.pitch + dPitch * t,
      });
    }
  }
  for (const st of source) {
    if (
      last &&
      Math.hypot(
          st.p[0] - last.p[0],
          st.p[1] - last.p[1],
          st.p[2] - last.p[2],
        ) < 0.05
    ) continue; // near-duplicate ring → degenerate triangles
    rings.push(st);
    last = st;
  }
  let arc = 0;
  for (const [ri, st] of rings.entries()) {
    if (ri > 0) {
      const prev = rings[ri - 1];
      arc += Math.hypot(
        st.p[0] - prev.p[0],
        st.p[1] - prev.p[1],
        st.p[2] - prev.p[2],
      );
    }
    // Catch-mouth flare: lateral scale ramps 1.35 → 1 over the first
    // 2 world units (heights untouched — the floor band widens and the
    // gutter walls converge downstream).
    const latScale = flareStart ? 1 + 0.35 * Math.max(0, 1 - arc / 2.0) : 1;
    // Frame from the true tangent: right = up × t, upV = t × right.
    const ty = Math.sin(-st.pitch);
    const th = Math.cos(st.pitch);
    const t = [Math.sin(st.yaw) * th, ty, Math.cos(st.yaw) * th];
    const rl = Math.hypot(t[2], t[0]) || 1;
    const right = [t[2] / rl, 0, -t[0] / rl];
    // up = t × right (NOT right × t — that hangs the gutters and walls
    // BELOW the floor; the straight repro masked it because the flat
    // middle of the profile sits at up=0 either way).
    const upV = [
      t[1] * right[2] - t[2] * right[1],
      t[2] * right[0] - t[0] * right[2],
      t[0] * right[1] - t[1] * right[0],
    ];
    for (const [lat, up] of CHANNEL_PROFILE) {
      // Width law (T1, measured): wall-side points shift OUT by the
      // station's widening; the flat floor stays put and the sheet
      // between stretches — the kit widens by moving walls, not by
      // scaling the section.
      const l0 = lat * latScale;
      const l = Math.abs(l0) > PROFILE_FLAT
        ? l0 + Math.sign(l0) * st.wShift
        : l0;
      vertices.push(
        st.p[0] + right[0] * l + upV[0] * up,
        st.p[1] + right[1] * l + upV[1] * up,
        st.p[2] + right[2] * l + upV[2] * up,
      );
    }
  }
  for (let r = 0; r < rings.length - 1; r++) {
    const a = r * N;
    const b = (r + 1) * N;
    for (let j = 0; j < N - 1; j++) {
      // Winding: Box3D mesh collision is ONE-SIDED. ribbonOf lays the
      // profile along right = up × t (−z for +x travel), so THIS order
      // faces the channel interior — verified by the isolated repro chain
      // (square → mini-ribbon → real ribbon; handedness matters).
      indices.push(a + j, b + j, b + j + 1);
      indices.push(a + j, b + j + 1, a + j + 1);
    }
  }
  return { kind: "mesh", vertices, indices };
}

/** Heights of the cliff block family (world units). */
function blockHeight(pieceId: string): number | null {
  if (pieceId.includes("cliff_blockQuarter")) return 0.25;
  if (pieceId.includes("cliff_blockHalf")) return 0.5;
  if (pieceId.includes("cliff_block")) return 1;
  // Island mass families: Platformer block voxels (measured 1.0 full /
  // 0.5 low courses; the 0.082 grass-lip overhang is cosmetic — the
  // box stays the 1x1 course like every mass family).
  if (/^platformer-kit:block-(grass|snow)-low/.test(pieceId)) return 0.5;
  if (/^platformer-kit:block-(grass|snow)/.test(pieceId)) return 1;
  return null;
}

/** Box proxies for an island's structural blocks (caps/decor are cosmetic
 * — the marble never touches them from the channel). */
export function islandProxies(
  placements: readonly Placement[],
): BoxProxy[] {
  const out: BoxProxy[] = [];
  for (const p of placements) {
    if (p.role !== "support") continue;
    const h = blockHeight(p.pieceId);
    if (h === null) continue;
    out.push({
      kind: "box",
      center: [p.pos[0], p.pos[1] + h / 2, p.pos[2]],
      half: [0.5, h / 2, 0.5],
    });
  }
  return out;
}
